//
// ============================================================================
// IMPORTS
// ============================================================================
//

// Types
import type { PendingSample } from "../../../services/api/domains/pending/pending.types";
import type { Visit } from "../../../services/api/domains/lis/lis.types";

//
// ============================================================================
// COMPOSABLE
// ============================================================================
//

/**
 * Validation Sample Selection Composable
 *
 * Manages selected sample state and related data.
 *
 * Responsibilities:
 * - Track currently selected sample
 * - Persist selected barcode to session storage
 * - Store sample-specific data (results, visit, flagging)
 * - Provide computed flags for selection status
 * - Clear selection and data on demand
 *
 * @param key - Unique key for state isolation (default: "default")
 */
export const useValidationSelection = (key: string = "default") => {
  //
  // ============================================================================
  // STATE
  // ============================================================================
  //

  // Currently selected sample (runtime only, not persisted)
  const selectedSample = useState<PendingSample | null>(`${key}.selectedSample`, () => null);

  // Selected sample barcode (persisted to sessionStorage)
  const selectedBarcode = useSessionState<string | null>(`${key}.selectedBarcode`, null);

  // Pending results for selected sample
  const selectedSampleResults = useState<any[]>(`${key}.selectedSampleResults`, () => []);

  // Visit data for selected sample (patient demographics, examinations)
  const selectedSampleVisit = useState<Visit | null>(`${key}.selectedSampleVisit`, () => null);

  // Flagging results for selected sample (clinical alerts)
  const selectedSampleFlagging = useState<any[]>(`${key}.selectedSampleFlagging`, () => []);

  // Loading flag: Prevents UI flicker during data fetch
  const loadingSampleData = useState<boolean>(`${key}.loadingSampleData`, () => false);

  //
  // ============================================================================
  // COMPUTED PROPERTIES
  // ============================================================================
  //

  // Check if a sample is currently selected
  const hasSelection = computed(() => !!selectedSample.value);

  // Check if selected sample has loaded data (results or visit)
  const hasData = computed(
    () => hasSelection.value && (selectedSampleResults.value.length > 0 || selectedSampleVisit.value !== null),
  );

  //
  // ============================================================================
  // METHODS
  // ============================================================================
  //

  /**
   * Set selected sample and persist barcode
   *
   * Purpose:
   * - Update selected sample state
   * - Persist barcode to sessionStorage for page refresh
   *
   * @param sample - Sample to select (null to clear)
   */
  const setSelectedSample = (sample: PendingSample | null): void => {
    selectedSample.value = sample;
    selectedBarcode.value = sample?.barcode || null;
  };

  /**
   * Clear all selected sample data
   *
   * Purpose:
   * - Reset selection when no samples available
   * - Clear after bulk operations (accept all, reject all)
   * - Clean state for fresh selection
   *
   * Clears:
   * - Selected sample object
   * - Persisted barcode
   * - Results, visit, flagging data
   */
  const clearSelection = (): void => {
    selectedSample.value = null;
    selectedBarcode.value = null;
    selectedSampleResults.value = [];
    selectedSampleVisit.value = null;
    selectedSampleFlagging.value = [];
  };

  /**
   * Clear data immediately (for rapid selection changes)
   *
   * Purpose:
   * - Clear stale data before fetching new sample data
   * - Prevent flashing old data during selection change
   * - Keep sample/barcode selection intact
   *
   * Clears:
   * - Results, visit, flagging data
   *
   * Preserves:
   * - Selected sample object
   * - Persisted barcode
   */
  const clearData = (): void => {
    selectedSampleResults.value = [];
    selectedSampleVisit.value = null;
    selectedSampleFlagging.value = [];
  };

  //
  // ============================================================================
  // PUBLIC API
  // ============================================================================
  //

  return {
    // State
    selectedSample,
    selectedSampleResults,
    selectedSampleVisit,
    selectedSampleFlagging,
    loadingSampleData,

    // Computed (readonly)
    selectedBarcode,
    hasSelection: readonly(hasSelection),
    hasData: readonly(hasData),

    // Methods
    setSelectedSample,
    clearSelection,
    clearData,
  };
};
